import rawCatalog from "../sets.json";
import type { Field, SetRecord } from "./types";

interface RawCatalogRecord {
  id?: unknown;
  name?: unknown;
  era?: unknown;
  eraId?: unknown;
  year?: unknown;
  releaseDate?: unknown;
  symbol?: unknown;
  scale?: unknown;
}

interface RawCatalogShape {
  sets?: unknown;
}

const raw = rawCatalog as RawCatalogShape;

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function parseSet(value: unknown): SetRecord | null {
  if (!isRecord(value)) return null;
  const record = value as RawCatalogRecord;
  if (
    typeof record.id !== "string" ||
    typeof record.name !== "string" ||
    typeof record.era !== "string" ||
    typeof record.eraId !== "string" ||
    typeof record.year !== "number" ||
    !Number.isInteger(record.year) ||
    (record.releaseDate !== null && typeof record.releaseDate !== "string") ||
    typeof record.symbol !== "string"
  ) {
    return null;
  }
  return {
    id: record.id,
    name: record.name,
    era: record.era,
    eraId: record.eraId,
    year: record.year,
    releaseDate: record.releaseDate,
    symbol: record.symbol,
    scale: typeof record.scale === "number" && Number.isFinite(record.scale) && record.scale > 0 ? record.scale : 1,
  };
}

const parsed = Array.isArray(raw.sets) ? raw.sets.map(parseSet).filter((set): set is SetRecord => set !== null) : [];

export const catalog: SetRecord[] = parsed;
export const catalogById = new Map(catalog.map((set) => [set.id, set]));

export interface EraOption {
  id: string;
  name: string;
  count: number;
}

export const eraOptions: EraOption[] = Array.from(
  catalog.reduce((map, set) => {
    const existing = map.get(set.eraId);
    if (existing) existing.count += 1;
    else map.set(set.eraId, { id: set.eraId, name: set.era, count: 1 });
    return map;
  }, new Map<string, EraOption>()).values(),
);

export const years = Array.from(new Set(catalog.map((set) => set.year))).sort((a, b) => a - b);

function symbolExtension(symbol: string): string {
  const match = symbol.match(/\.([a-z0-9]+)$/i);
  return match?.[1].toLowerCase() ?? "svg";
}

export function symbolUrl(set: SetRecord): string {
  const base = import.meta.env.BASE_URL || "/";
  const extension = import.meta.env.DEV ? symbolExtension(set.symbol) : "png";
  return `${base}symbols/${encodeURIComponent(set.id)}.${extension}`;
}

export function symbolScale(set: SetRecord): number {
  return set.scale ?? 1;
}

export function fieldValue(set: SetRecord, field: Field): string {
  if (field === "name") return set.name;
  if (field === "era") return set.era;
  return String(set.year);
}

export function displayDate(set: SetRecord): string {
  if (!set.releaseDate) return String(set.year);
  const parts = set.releaseDate.split("/").map(Number);
  if (parts.length !== 3 || parts.some((part) => !Number.isInteger(part))) return set.releaseDate;
  const [year, month, day] = parts;
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) return set.releaseDate;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(date);
}

export function resolveEraId(input: string, options: EraOption[] = eraOptions): string | null {
  const normalized = input.trim().toLocaleLowerCase();
  const option = options.find((candidate) => candidate.id.toLocaleLowerCase() === normalized || candidate.name.toLocaleLowerCase() === normalized);
  return option?.id ?? null;
}
